import type { PureQueryOptions } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import invariant from 'tiny-invariant';
import { v4 } from 'uuid';
import { z } from 'zod';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import {
  LifecycleStatus,
  type PictureCreateInput,
  type PictureInitialParentType,
} from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { PictureUploadDefaultValuesForm } from '~/components/upload/file/picture-upload-v4/default-values-form';
import { PictureUploadQueueItem } from '~/components/upload/file/picture-upload-v4/picture-upload-queue-item';
import { PictureDropzone } from '~/components/upload/supporting-object/picture-dropzone';
import { useRefetchQueriesV4 } from '~/hooks/apollo';
import { useZodForm } from '~/utils/forms';
import { uploadFile } from '~/utils/modules/file';
import { ppStringOrNull } from '~/utils/zod';

const PICTURE_UPLOAD_V4 = graphql(`
  mutation PictureUploadV4($input: PictureCreateInput!) {
    pictureCreate(input: $input) {
      path
      token
    }
  }
`);

const pictureUploadQueueStatuses = ['idle', 'uploading'] as const;
export type PictureUploadQueueStatus =
  (typeof pictureUploadQueueStatuses)[number];

const pictureUploadQueueItemStatuses = [
  'pending',
  'uploading',
  'success',
  'failed',
] as const;
type PictureUploadQueueItemStatus =
  (typeof pictureUploadQueueItemStatuses)[number];

const pictureUploadSchema = z.object({
  defaultValues: z.object({
    author: z.string(),
  }),
  items: z.array(
    z.object({
      tmpId: z.string(),
      status: z.enum(pictureUploadQueueItemStatuses),
      file: z.instanceof(File),
      values: z.object({
        name: z.string().min(1),
        description: z.string().min(1),
        author: z.preprocess(ppStringOrNull, z.string().nullable()),
        type: z.string(),
      }),
    }),
  ),
});

type PictureUploadQueueItem = {
  tmpId: string;
  status: PictureUploadQueueItemStatus;
  file: File;
  values: {
    name: string;
    description: string;
    author: string;
    type: string;
  };
};

export type PictureUploadAutofillValues = {
  name?: string;
  description?: string;
  author?: string;
};

export type PictureUploadQueueFormValues = {
  autofillValues?: PictureUploadAutofillValues;
  defaultValues: {
    author: string;
    autofill: boolean;
  };
  items: PictureUploadQueueItem[];
};

function initialPictureValues(file: File): PictureUploadQueueItem {
  return {
    tmpId: v4(),
    status: 'pending',
    file,
    values: {
      name: file.name,
      description: '',
      author: '',
      type: 'undefined',
    },
  };
}

function initialValues(
  autofill?: PictureUploadAutofillValues,
): PictureUploadQueueFormValues {
  return {
    autofillValues: autofill,
    defaultValues: {
      author: '',
      autofill: false,
    },
    items: [],
  };
}

type PictureUploadV4Props = {
  parentType: PictureInitialParentType;
  parentId: number;
  limit?: number;
  refetchQueries: PureQueryOptions[];
  autofillValues?: PictureUploadAutofillValues;
};

export function PictureUploadV4(props: PictureUploadV4Props) {
  return (
    <ApolloProviderV4>
      <PictureUploadV4Inner {...props} />
    </ApolloProviderV4>
  );
}

function PictureUploadV4Inner({
  parentType,
  parentId,
  limit,
  refetchQueries,
  autofillValues,
}: PictureUploadV4Props) {
  const [pictureCreate] = useMutation(PICTURE_UPLOAD_V4);
  const [refetch] = useRefetchQueriesV4(refetchQueries);
  const [isUploading, setIsUploading] = useState(false);

  const form = useZodForm({
    schema: pictureUploadSchema,
    values: initialValues(autofillValues),
  });
  const itemFieldArray = useFieldArray({
    control: form.control,
    name: 'items',
  });

  const queueItems = form.watch('items');

  function handleDrop(files: File[]) {
    itemFieldArray.append(files.map(initialPictureValues));
  }

  function handleRemove(index: number) {
    itemFieldArray.remove(index);
  }

  function setItemStatus(index: number, status: PictureUploadQueueItemStatus) {
    form.setValue(`items.${index}.status`, status);
  }

  async function uploadItem(
    index: number,
    item: z.infer<typeof pictureUploadSchema>['items'][number],
  ) {
    const input: PictureCreateInput = {
      picture: {
        parentType,
        parentId,
        ...item.values,
        lifecycleStatus: LifecycleStatus.Published,
      },
    };

    try {
      setItemStatus(index, 'uploading');
      const res = await pictureCreate({ variables: { input } });
      const createResult = res.data?.pictureCreate;
      const path = createResult?.path;
      const token = createResult?.token;
      invariant(path && token, 'Missing upload path or token');

      const uploadResult = await uploadFile({ path, token, file: item.file });
      if (!uploadResult) {
        throw new Error('POST file failed');
      }
      setItemStatus(index, 'success');
    } catch (err) {
      console.log('Error uploading file', err);
      setItemStatus(index, 'failed');
    }
  }

  const handleSubmit = form.handleSubmit(async values => {
    const items = values.items.filter(
      item => item.status === 'pending' || item.status === 'failed',
    );

    setIsUploading(true);
    for (const item of items) {
      const index = values.items.findIndex(i => i.tmpId === item.tmpId);
      await uploadItem(index, item);
    }
    setIsUploading(false);
    await refetch();
  });

  const hasPendingItems = queueItems.some(
    item => item.status === 'pending' || item.status === 'failed',
  );

  const numFailedItems = queueItems.reduce(
    (acc, item) => (item.status === 'failed' ? acc + 1 : acc),
    0,
  );

  const withinItemLimit =
    !limit ||
    queueItems.filter(item => item.status !== 'failed').length < limit;

  const canStartUpload = hasPendingItems && withinItemLimit;

  return (
    <div className="space-y-4">
      <PictureDropzone onDrop={handleDrop} hasFiles={!!queueItems.length} />

      <FormProvider {...form}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <PictureUploadDefaultValuesForm disabled={isUploading} />

            {queueItems.map((item, index) => (
              <PictureUploadQueueItem
                key={item.tmpId}
                index={index}
                onRemove={() => handleRemove(index)}
              />
            ))}
          </div>

          <div className="text-center space-y-2">
            <Button
              type="submit"
              color="primary"
              disabled={!canStartUpload}
              loading={isUploading}
            >
              Upload Files
            </Button>

            {numFailedItems > 0 && (
              <div className="text-error">
                <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                {numFailedItems} items failed to upload.
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
